import {
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Article, articlesApi } from '@texas/api/endpoints/articlesApi';
import { Branch, branchesApi } from '@texas/api/endpoints/branchesApi';
import { branchRegionsApi } from '@texas/api/endpoints/branchRegionsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { reactEvents } from '@bridge/reactEvents';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { Icons } from '@texas/components/shared/Icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ManagementCard } from './ManagementCard';
import { articleManageEvents } from './events';

interface Props {
  article: Article;
  onUpdated: () => void;
}

export function ArticleBranchesCard({ article, onUpdated }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const modal = useDisclosure();
  const [query, setQuery] = useState('');
  const [selectedBranchIds, setSelectedBranchIds] = useState<Set<number>>(
    new Set(),
  );
  const {
    data: branches,
    loading: branchesLoading,
    error: branchesError,
    refetch: refetchBranches,
  } = useApiResource(branchesApi.getBranches);
  const {
    data: branchRegions,
    loading: branchRegionsLoading,
    error: branchRegionsError,
    refetch: refetchBranchRegions,
  } = useApiResource(branchRegionsApi.getAll);
  const {
    request: updateBranches,
    loading: saving,
    error: saveError,
  } = useApiRequest(articlesApi.updateBranches);

  useEffect(() => {
    refetchBranches();
    refetchBranchRegions('');
  }, [refetchBranchRegions, refetchBranches]);

  useEffect(() => {
    setSelectedBranchIds(new Set(article.branches.map((branch) => branch.id)));
  }, [article.branches]);

  const selectedBranches = useMemo(
    () =>
      (branches ?? []).filter((branch) =>
        article.branches.some((selected) => selected.id === branch.id),
      ),
    [article.branches, branches],
  );

  const selectedBranchesByRegion = useMemo(() => {
    const assignedBranchIds = new Set(
      (branchRegions ?? []).flatMap((region) =>
        region.branches.map((branch) => branch.id),
      ),
    );
    const regions = (branchRegions ?? [])
      .map((region) => ({
        id: region.id.toString(),
        name: region.name,
        branches: selectedBranches.filter((branch) =>
          region.branches.some((regionBranch) => regionBranch.id === branch.id),
        ),
      }))
      .filter((region) => region.branches.length > 0);
    const unassignedBranches = selectedBranches.filter(
      (branch) => !assignedBranchIds.has(branch.id),
    );

    if (unassignedBranches.length > 0) {
      regions.push({
        id: 'unassigned',
        name: t('article.management.unassignedBranches'),
        branches: unassignedBranches,
      });
    }

    return regions;
  }, [branchRegions, selectedBranches, t]);

  const visibleBranches = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return branches ?? [];
    return (branches ?? []).filter(
      (branch) =>
        branch.name.toLowerCase().includes(normalizedQuery) ||
        branch.identifier.toLowerCase().includes(normalizedQuery),
    );
  }, [branches, query]);

  const openModal = () => {
    setQuery('');
    setSelectedBranchIds(new Set(article.branches.map((branch) => branch.id)));
    modal.onOpen();
  };

  const toggleBranch = (branchId: number) => {
    setSelectedBranchIds((current) => {
      const next = new Set(current);
      if (next.has(branchId)) next.delete(branchId);
      else next.add(branchId);
      return next;
    });
  };

  const save = async () => {
    const response = await updateBranches(article.id, {
      branchIds: Array.from(selectedBranchIds),
    });
    if (response.hasError) return;

    toast({
      title: t('article.management.branchesSaved'),
      status: 'success',
      position: 'bottom-right',
    });
    articleManageEvents.branchesModified.dispatch();
    reactEvents.articleUpdated.dispatch(article.id);
    onUpdated();
    modal.onClose();
  };

  const branchCount = branches?.length ?? 0;

  return (
    <>
      <Box mt={5}>
        <ManagementCard
          title={t('article.management.branches')}
          description={t('article.management.branchesDescription')}
          action={
            <Button
              leftIcon={<Icons.Pencil />}
              variant="outline"
              onClick={openModal}
            >
              {t('article.management.editBranches')}
            </Button>
          }
        >
          {branchesError && <ErrorDetails error={branchesError} />}
          {branchRegionsError && <ErrorDetails error={branchRegionsError} />}
          {branchesLoading || branchRegionsLoading ? (
            <Spinner />
          ) : (
            <Box border="1px solid" borderColor="articleManagement.border">
              <Grid
                templateColumns={{ base: '1fr', md: '180px minmax(0, 1fr)' }}
                gap={4}
                px={4}
                py={2}
                bg="articleManagement.canvas"
                borderBottom="1px solid"
                borderColor="articleManagement.border"
              >
                <Text variant="article-management-table-header">
                  {t('general.name')}
                </Text>
                <Text variant="article-management-table-header">
                  {t('general.branches')}
                </Text>
              </Grid>
              {selectedBranchesByRegion.map((region, index) => (
                <Grid
                  key={region.id}
                  templateColumns={{
                    base: '1fr',
                    md: '180px minmax(0, 1fr)',
                  }}
                  gap={4}
                  minH="52px"
                  px={4}
                  py={3}
                  borderBottom={
                    index < selectedBranchesByRegion.length - 1
                      ? '1px solid'
                      : undefined
                  }
                  borderColor="articleManagement.divider"
                >
                  <Text variant="article-management-region-name">
                    {region.name}
                  </Text>
                  <Flex align="center" gap={2} wrap="wrap">
                    {region.branches.map((branch) => (
                      <BranchBadge key={branch.id} branch={branch} />
                    ))}
                  </Flex>
                </Grid>
              ))}
            </Box>
          )}
        </ManagementCard>
      </Box>

      <Modal
        isOpen={modal.isOpen}
        onClose={modal.onClose}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('article.management.editBranches')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text color="articleManagement.mutedText" fontSize="sm" mb={5}>
              {t('article.management.editBranchesDescription')}
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icons.Search color="articleManagement.mutedText" />
              </InputLeftElement>
              <Input
                value={query}
                placeholder={t('article.management.searchBranches')}
                onChange={(event) => setQuery(event.target.value)}
              />
            </InputGroup>
            <Flex align="center" justify="space-between" mt={4} mb={3}>
              <Text fontSize="sm">
                <Text as="span" fontWeight="600">
                  {t('article.management.branchesSelected', {
                    count: selectedBranchIds.size,
                  })}
                </Text>{' '}
                ·{' '}
                {t('article.management.branchesAvailable', {
                  count: branchCount,
                })}
              </Text>
              <Flex gap={4}>
                <Button
                  variant="link"
                  color="articleManagement.accent"
                  fontSize="sm"
                  onClick={() =>
                    setSelectedBranchIds(
                      new Set((branches ?? []).map((branch) => branch.id)),
                    )
                  }
                >
                  {t('general.selectAll')}
                </Button>
                <Button
                  variant="link"
                  color="articleManagement.accent"
                  fontSize="sm"
                  onClick={() => setSelectedBranchIds(new Set())}
                >
                  {t('general.clear')}
                </Button>
              </Flex>
            </Flex>
            {saveError && <ErrorDetails error={saveError} />}
            <Grid
              templateColumns="40px minmax(0, 1fr) 120px"
              alignItems="center"
              h="38px"
              px={3}
              bg="articleManagement.canvas"
              border="1px solid"
              borderColor="articleManagement.border"
              color="articleManagement.mutedText"
              fontFamily="barlow"
              fontSize="xs"
              fontWeight="600"
              letterSpacing="0.04em"
            >
              <Box />
              <Text>{t('general.name').toUpperCase()}</Text>
              <Text>{t('article.management.identifier').toUpperCase()}</Text>
            </Grid>
            <Box maxH="416px" overflowY="auto">
              {visibleBranches.map((branch) => {
                const selected = selectedBranchIds.has(branch.id);
                return (
                  <Grid
                    key={branch.id}
                    templateColumns="40px minmax(0, 1fr) 120px"
                    alignItems="center"
                    minH="52px"
                    px={3}
                    bg={
                      selected
                        ? 'articleManagement.selectedSurface'
                        : 'articleManagement.surface'
                    }
                    borderX="1px solid"
                    borderBottom="1px solid"
                    borderColor="articleManagement.divider"
                    cursor="pointer"
                    onClick={() => toggleBranch(branch.id)}
                  >
                    <Checkbox
                      isChecked={selected}
                      colorScheme="orange"
                      pointerEvents="none"
                    />
                    <Text fontSize="sm" fontWeight={selected ? '600' : '400'}>
                      {branch.name}
                    </Text>
                    <Text color="articleManagement.mutedText" fontSize="sm">
                      {branch.identifier}
                    </Text>
                  </Grid>
                );
              })}
            </Box>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Flex align="center" gap={2}>
              <Box boxSize="7px" borderRadius="full" bg="status.success" />
              <Text color="articleManagement.mutedText" fontSize="xs">
                {t('article.management.branchesSelected', {
                  count: selectedBranchIds.size,
                })}
              </Text>
            </Flex>
            <Flex gap={3}>
              <Button onClick={modal.onClose}>{t('general.cancel')}</Button>
              <Button
                leftIcon={<Icons.Checkmark />}
                isDisabled={selectedBranchIds.size === 0}
                isLoading={saving}
                variant="texas-solid"
                onClick={save}
              >
                {t('article.management.saveBranches')}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function BranchBadge({ branch }: { branch: Branch }) {
  return (
    <Flex align="center" px={2} py={1} bg="articleManagement.subtleSurface">
      <Text variant="article-management-branch">
        {branch.name} ({branch.identifier})
      </Text>
    </Flex>
  );
}
